import { extend } from 'vee-validate';
/**
 * Allows somewhat simple inclusion of global validators
 * that are like vee-validate v3 (https://vee-validate.logaretm.com/v3/guide/basics.html#messages)
 * but they are v4 global validators (https://vee-validate.logaretm.com/v4/guide/global-validators)
 *
 * The purpose of this definition should allow us to almost seemlessly transition
 * between the two types of global validators, with minimal changes.
 *
 * old examples:
 * ```
 *   import { email } from 'vee-validate/dist/rules'
 *   // object style
 *   extend('required', {
 *     ...required,
 *     message: '{_field_} is required'
 *   })
 *   extend('validDomain', {
 *     validate: (value)=>isValidDomain(value),
 *     message: 'Please enter a valid domain'
 *   })
 *   // function style
 *   extend('password', value => isValidPassword(value) || 'Password is invalid')
 *   extend('email', email)
 * ```
 *
 * new examples:
 * ```
 *   import { required, email, min } from '@vee-validate/rules'
 *   // function style only
 *   defineRule('required', (value, params, { field }) => {
 *     return required(value) || `${field} is required`
 *   })
 *   defineRule('validDomain', (value) => {
 *     return isValidDomain(value) || 'Please enter a valid domain'
 *   })
 *   defineRule('password', (value, [], { field }) => isValidPassword(value) || `${field} is invalid`)
 *   defineRule('email', email) // uses default error message "${field.label || field.name} is not valid"
 * ```
*/
export default function defineRule(ruleName, validationFunction) {
    // identify this rule and a shim rule, to not collide with v3 global validation rules
    const namespacedRuleName = `vv4-${ruleName}`;
    extend(namespacedRuleName, {
        validate: (value, params) => {
            // because param keys are not passed, params will be a string array
            // see: https://vee-validate.logaretm.com/v3/guide/basics.html#rule-arguments
            const result = validationFunction(value, params, {
                // vee-validate v3 will replace the '{_field_}' string with the field name
                // and the '{_value_}' string with the value name at validation error message creation time
                field: '{_field_}',
                value: '{_value_}',
                rule: { name: ruleName, params },
            });
            // return true or error string
            return result;
        },
        // special case: compute 'required' for required fields
        computesRequired: ruleName === 'required',
        // default config is not lazy validation
        lazy: false,
    });
}
